import {
  jobTrackerFixtureBoard,
  jobTrackerFixtureEntries,
  jobTrackerFixtureLinkPreview,
  jobTrackerFixtureRecentlyViewed,
} from './jobTrackerFixture';

export const BOARD_PAGE_SIZE = 10;

function encodeMockCursor(entry) {
  if (!entry) return null;
  const changedAt = entry.stage_changed_at || '';
  const raw = `${changedAt}|${entry.id}`;
  if (typeof btoa === 'function') {
    return btoa(raw).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  }
  return Buffer.from(raw, 'utf8')
    .toString('base64')
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

function decodeMockCursor(cursor) {
  if (!cursor || typeof cursor !== 'string') return null;
  const padded = cursor.replace(/-/g, '+').replace(/_/g, '/');
  const pad = padded.length % 4;
  const withPad = pad ? padded + '='.repeat(4 - pad) : padded;
  let raw;
  try {
    raw =
      typeof atob === 'function'
        ? atob(withPad)
        : Buffer.from(withPad, 'base64').toString('utf8');
  } catch (e) {
    return null;
  }
  const sep = raw.indexOf('|');
  if (sep === -1) return null;
  return {
    stage_changed_at: raw.slice(0, sep),
    id: raw.slice(sep + 1),
  };
}

function compareMockIdsDesc(aId, bId) {
  return String(bId).localeCompare(String(aId), undefined, { numeric: true });
}

function isMockEntryAfterCursor(entry, decoded) {
  const changedAt = entry.stage_changed_at || '';
  if (changedAt < decoded.stage_changed_at) return true;
  if (changedAt > decoded.stage_changed_at) return false;
  return compareMockIdsDesc(entry.id, decoded.id) > 0;
}

function sortMockEntriesNewestFirst(entries) {
  return [...entries].sort((a, b) => {
    const aAt = a.stage_changed_at || '';
    const bAt = b.stage_changed_at || '';
    if (aAt !== bAt) {
      return aAt < bAt ? 1 : -1;
    }
    return compareMockIdsDesc(a.id, b.id);
  });
}

function pageMockStage(stage, cursor, limit) {
  const sorted = sortMockEntriesNewestFirst(
    mockEntries.filter((entry) => entry.stage === stage)
  );
  const decoded = decodeMockCursor(cursor);
  let start = 0;
  if (decoded) {
    start = sorted.findIndex((entry) => isMockEntryAfterCursor(entry, decoded));
    if (start < 0) start = sorted.length;
  }
  const slice = sorted.slice(start, start + limit);
  const hasMore = start + limit < sorted.length;
  const nextCursor =
    hasMore && slice.length ? encodeMockCursor(slice[slice.length - 1]) : null;
  return {
    data: slice.map(cloneEntry),
    has_more: hasMore,
    next_cursor: nextCursor,
  };
}

let useMock = process.env.JOB_TRACKER_MOCK === 'true';
let mockError = null;
let mockEntries = jobTrackerFixtureEntries.map((entry) => ({ ...entry }));
let mockRecentlyViewed = jobTrackerFixtureRecentlyViewed.data.map((item) => ({
  ...item,
}));
let mockLinkPreviewOverride = null;
let mockEntrySeq = 0;

function cloneRecentlyViewedItem(item) {
  return { ...item };
}

function findMockRecentlyViewed(body) {
  if (!body) return null;
  if (body.job_id != null) {
    return mockRecentlyViewed.find(
      (item) =>
        item.job_id != null && String(item.job_id) === String(body.job_id)
    );
  }
  if (body.ari_job_id != null) {
    return mockRecentlyViewed.find(
      (item) =>
        item.ari_job_id != null &&
        String(item.ari_job_id) === String(body.ari_job_id)
    );
  }
  return null;
}

function removeMockRecentlyViewed(body) {
  if (!body) return;
  if (body.job_id != null) {
    mockRecentlyViewed = mockRecentlyViewed.filter(
      (item) =>
        item.job_id == null || String(item.job_id) !== String(body.job_id)
    );
    return;
  }
  if (body.ari_job_id != null) {
    mockRecentlyViewed = mockRecentlyViewed.filter(
      (item) =>
        item.ari_job_id == null ||
        String(item.ari_job_id) !== String(body.ari_job_id)
    );
  }
}

/** LinkedIn acceptance-criteria preview (package F5). */
const LINKEDIN_FATHOM_PREVIEW = {
  fetched: true,
  title: 'Webflow developer for marketing site rebuild',
  company: 'Fathom Labs',
  source_site: 'LinkedIn',
  location: 'Remote',
};

/**
 * Enable or disable Job tracker mock responses (no real HTTP).
 * Used in local development and Jest when the API is not available.
 */
export function setJobTrackerMock(enabled) {
  useMock = Boolean(enabled);
  if (enabled) {
    mockEntries = jobTrackerFixtureEntries.map((entry) => ({ ...entry }));
    mockRecentlyViewed = jobTrackerFixtureRecentlyViewed.data.map((item) => ({
      ...item,
    }));
  }
}

export function isJobTrackerMock() {
  return useMock;
}

/**
 * When mock mode is on, the next API call rejects with this error (or Error).
 * Pass null to clear. Useful for optimistic-move rollback tests.
 */
export function setJobTrackerMockError(error) {
  mockError = error;
}

function maybeReject() {
  if (!mockError) {
    return null;
  }
  const error = mockError;
  mockError = null;
  return Promise.reject(error instanceof Error ? error : new Error(error));
}

function cloneEntry(entry) {
  return {
    ...entry,
    application: entry.application ? { ...entry.application } : null,
    client: entry.client ? { ...entry.client } : null,
  };
}

function findMockEntry(id) {
  return mockEntries.find((entry) => String(entry.id) === String(id));
}

function countsFromMock() {
  return mockEntries.reduce(
    (counts, entry) => {
      if (counts[entry.stage] !== undefined) {
        counts[entry.stage] += 1;
      }
      return counts;
    },
    {
      saved: 0,
      applied: 0,
      in_talks: 0,
      proposal_sent: 0,
      won: 0,
    }
  );
}

export function getBoard(client, { perStage = BOARD_PAGE_SIZE } = {}) {
  if (useMock) {
    const rejected = maybeReject();
    if (rejected) return rejected;
    const limit = Math.max(
      1,
      Math.min(50, Number(perStage) || BOARD_PAGE_SIZE)
    );
    const data = [];
    const pagination = {};
    ['saved', 'applied', 'in_talks', 'proposal_sent', 'won'].forEach(
      (stage) => {
        const page = pageMockStage(stage, null, limit);
        page.data.forEach((entry) => data.push(entry));
        pagination[stage] = {
          has_more: page.has_more,
          next_cursor: page.next_cursor,
        };
      }
    );
    return Promise.resolve({
      data,
      meta: {
        counts: countsFromMock(),
        pagination,
        per_stage: limit,
      },
    });
  }
  const query =
    perStage && perStage !== BOARD_PAGE_SIZE ? `?per_stage=${perStage}` : '';
  return client.get(`/job-tracker${query}`);
}

/**
 * Next page of entries for one board stage.
 * @param {object} client
 * @param {string} stage
 * @param {{ cursor?: string|null, limit?: number }} [options]
 */
export function getStagePage(
  client,
  stage,
  { cursor = null, limit = BOARD_PAGE_SIZE } = {}
) {
  if (useMock) {
    const rejected = maybeReject();
    if (rejected) return rejected;
    const pageLimit = Math.max(
      1,
      Math.min(50, Number(limit) || BOARD_PAGE_SIZE)
    );
    const page = pageMockStage(stage, cursor, pageLimit);
    return Promise.resolve({
      data: page.data,
      meta: {
        stage,
        has_more: page.has_more,
        next_cursor: page.next_cursor,
      },
    });
  }
  const params = [`limit=${limit || BOARD_PAGE_SIZE}`];
  if (cursor) {
    params.push(`cursor=${encodeURIComponent(cursor)}`);
  }
  return client.get(`/job-tracker/stages/${stage}?${params.join('&')}`);
}

export function createEntry(client, body) {
  if (useMock) {
    const rejected = maybeReject();
    if (rejected) return rejected;

    if (body && body.url) {
      const existingByUrl = mockEntries.find(
        (entry) => entry.url && entry.url === body.url
      );
      if (existingByUrl) {
        return Promise.resolve(cloneEntry(existingByUrl));
      }
    }

    if (body && body.job_id) {
      const existingTwine = mockEntries.find(
        (entry) =>
          entry.job_id != null && String(entry.job_id) === String(body.job_id)
      );
      if (existingTwine) {
        removeMockRecentlyViewed(body);
        return Promise.resolve(cloneEntry(existingTwine));
      }
    }

    if (body && body.ari_job_id) {
      const existingAri = mockEntries.find(
        (entry) =>
          entry.ari_job_id != null &&
          String(entry.ari_job_id) === String(body.ari_job_id)
      );
      if (existingAri) {
        removeMockRecentlyViewed(body);
        return Promise.resolve(cloneEntry(existingAri));
      }
    }

    const fromRecentlyViewed = findMockRecentlyViewed(body);
    const now = new Date().toISOString();
    mockEntrySeq += 1;
    const entry = {
      id: `entry-mock-${Date.now()}-${mockEntrySeq}`,
      stage: body.stage || 'saved',
      source: body.job_id
        ? 'twine'
        : body.ari_job_id
        ? 'ari'
        : body.source || 'manual',
      source_site:
        body.source_site ||
        (fromRecentlyViewed && fromRecentlyViewed.source_site) ||
        null,
      job_id: body.job_id || null,
      ari_job_id: body.ari_job_id || null,
      url: body.url || null,
      title:
        body.title ||
        (fromRecentlyViewed && fromRecentlyViewed.title) ||
        'Untitled job',
      company:
        body.company ||
        (fromRecentlyViewed && fromRecentlyViewed.company) ||
        null,
      location: body.location || null,
      rate: null,
      skill: null,
      user_edited_at: null,
      is_available: true,
      closes_at: null,
      application: null,
      hired_on_twine: false,
      client: null,
      saved_at: now,
      stage_changed_at: now,
      created_at: now,
      updated_at: now,
    };
    removeMockRecentlyViewed(body);
    mockEntries = [entry, ...mockEntries];
    return Promise.resolve(cloneEntry(entry));
  }
  return client.post('/job-tracker/entries', { data: body });
}

export function updateEntryStage(client, id, stage) {
  if (useMock) {
    const rejected = maybeReject();
    if (rejected) return rejected;
    const entry = findMockEntry(id);
    if (!entry) {
      return Promise.reject(new Error('Entry not found'));
    }
    const updated = {
      ...entry,
      stage,
      stage_changed_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };
    mockEntries = mockEntries.map((item) =>
      String(item.id) === String(id) ? updated : item
    );
    return Promise.resolve(cloneEntry(updated));
  }
  return client.put(`/job-tracker/entries/${id}/stage`, { data: { stage } });
}

export function updateEntry(client, id, body) {
  if (useMock) {
    const rejected = maybeReject();
    if (rejected) return rejected;
    const entry = findMockEntry(id);
    if (!entry) {
      return Promise.reject(new Error('Entry not found'));
    }
    const updated = {
      ...entry,
      ...body,
      user_edited_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };
    mockEntries = mockEntries.map((item) =>
      String(item.id) === String(id) ? updated : item
    );
    return Promise.resolve(cloneEntry(updated));
  }
  return client.patch(`/job-tracker/entries/${id}`, { data: body });
}

export function deleteEntry(client, id) {
  if (useMock) {
    const rejected = maybeReject();
    if (rejected) return rejected;
    mockEntries = mockEntries.filter(
      (entry) => String(entry.id) !== String(id)
    );
    return Promise.resolve(null);
  }
  return client.delete(`/job-tracker/entries/${id}`);
}

export function getRecentlyViewed(client) {
  if (useMock) {
    const rejected = maybeReject();
    if (rejected) return rejected;
    return Promise.resolve({
      data: mockRecentlyViewed.map(cloneRecentlyViewedItem),
    });
  }
  return client.get('/job-tracker/recently-viewed');
}

/**
 * Viewed / board state for listing cards.
 * @param {object} client
 * @param {{ jobIds?: number[], ariJobIds?: number[] }} ids
 * @returns {Promise<{ data: { jobs: object, ari_jobs: object } }>}
 */
export function getJobStates(client, { jobIds = [], ariJobIds = [] } = {}) {
  if (useMock) {
    const rejected = maybeReject();
    if (rejected) return rejected;
    return Promise.resolve({ data: { jobs: {}, ari_jobs: {} } });
  }
  const params = [];
  if (jobIds.length) params.push(`job_ids=${jobIds.join(',')}`);
  if (ariJobIds.length) params.push(`ari_job_ids=${ariJobIds.join(',')}`);
  return client.get(`/job-tracker/job-states?${params.join('&')}`);
}

export function postRecentlyViewed(client, body) {
  if (useMock) {
    const rejected = maybeReject();
    if (rejected) return rejected;
    return Promise.resolve(null);
  }
  return client.post('/job-tracker/recently-viewed', { data: body });
}

export function clearRecentlyViewed(client) {
  if (useMock) {
    const rejected = maybeReject();
    if (rejected) return rejected;
    mockRecentlyViewed = [];
    return Promise.resolve(null);
  }
  return client.delete('/job-tracker/recently-viewed');
}

/**
 * Override the next link-preview mock response (tests / local demos).
 * Pass null to clear. Shape matches POST /job-tracker/link-preview.
 */
export function setJobTrackerMockLinkPreview(preview) {
  mockLinkPreviewOverride = preview;
}

export function fetchLinkPreview(client, url) {
  if (useMock) {
    const rejected = maybeReject();
    if (rejected) return rejected;

    if (mockLinkPreviewOverride) {
      const preview = { ...mockLinkPreviewOverride, url };
      mockLinkPreviewOverride = null;
      return Promise.resolve(preview);
    }

    if (
      typeof url === 'string' &&
      url.indexOf('linkedin.com/jobs/view/4051928377') !== -1
    ) {
      return Promise.resolve({
        ...LINKEDIN_FATHOM_PREVIEW,
        url,
      });
    }

    return Promise.resolve({
      ...jobTrackerFixtureLinkPreview,
      url,
    });
  }
  return client.post('/job-tracker/link-preview', { data: { url } });
}

/** Reset mock store to the static fixture (for tests). */
export function resetJobTrackerMock() {
  mockEntries = jobTrackerFixtureEntries.map((entry) => ({ ...entry }));
  mockRecentlyViewed = jobTrackerFixtureRecentlyViewed.data.map((item) => ({
    ...item,
  }));
  mockError = null;
  mockLinkPreviewOverride = null;
  mockEntrySeq = 0;
}

export { jobTrackerFixtureBoard };
